export const normalizeKey = (s: string): string => {
    return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "")
}

export const safeNumber = (v: any): number | null => {
    if (v === null || v === undefined || v === "" || v === '""') return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
}

export const findColumnIndex = (columns: string[], candidates: string[]): number => {
    if (!Array.isArray(columns)) return -1
    const normalizedColumns = columns.map(normalizeKey)

    for (const candidate of candidates) {
        const normalizedCandidate = normalizeKey(candidate)
        const idx = normalizedColumns.indexOf(normalizedCandidate)
        if (idx !== -1) return idx
    }
    return -1
}

export const findColumnIndexByRegex = (columns: string[], regexes: RegExp[]): number => {
    if (!Array.isArray(columns)) return -1
    const normalizedColumns = columns.map(normalizeKey)

    for (const re of regexes) {
        const idx = normalizedColumns.findIndex(c => re.test(c))
        if (idx !== -1) return idx
    }
    return -1
}

export const parseMonthToValue = (raw: any): number => {
    if (!raw) return 0
    const s = String(raw).trim()

    const iso = s.match(/^(\d{4})\s*[-/]\s*(\d{1,2})\s*[-/]\s*(\d{1,2})/)
    if (iso) {
        const y = parseInt(iso[1], 10)
        const m = parseInt(iso[2], 10)
        if (Number.isFinite(y) && Number.isFinite(m) && m >= 1 && m <= 12) return y * 100 + m
    }

    if (s.includes("/")) {
        const parts = s.split("/").map(p => String(p).trim()).filter(Boolean)
        if (parts.length === 2) {
            const m = parseInt(parts[0], 10)
            const y = parseInt(parts[1].slice(0, 4), 10)
            if (Number.isFinite(y) && Number.isFinite(m) && m >= 1 && m <= 12) {
                return y * 100 + m
            }
        }
    }

    const monthMap: Record<string, number> = {
        jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3,
        apr: 4, april: 4, may: 5, jun: 6, june: 6,
        jul: 7, july: 7, aug: 8, august: 8,
        sep: 9, sept: 9, september: 9, oct: 10, october: 10,
        nov: 11, november: 11, dec: 12, december: 12
    }

    const parts = s.split(/\s+/)
    if (parts.length === 2) {
        const monthName = parts[0].toLowerCase()
        const year = parseInt(parts[1], 10)
        const month = monthMap[monthName]
        if (Number.isFinite(year) && month) return year * 100 + month
    }

    const ym = s.match(/^(\d{4})\s*[-/]\s*(\d{1,2})$/)
    if (ym) {
        const y = parseInt(ym[1], 10)
        const m = parseInt(ym[2], 10)
        if (Number.isFinite(y) && Number.isFinite(m) && m >= 1 && m <= 12) return y * 100 + m
    }

    return 0
}

export const expandMonthName = (monthStr: string): string => {
    if (!monthStr) return "";
    const MONTH_MAP: Record<string, string> = {
        Jan: "January", Feb: "February", Mar: "March", Apr: "April",
        May: "May", Jun: "June", Jul: "July", Aug: "August",
        Sep: "September", Oct: "October", Nov: "November", Dec: "December"
    };
    const [abbr, year] = monthStr.split(" ");
    const fullMonth = MONTH_MAP[abbr] || abbr;
    return year ? `${fullMonth} ${year}` : fullMonth;
};

export const formatMonthValue = (value: number): string => {
    if (!value || value < 100) return ''
    const year = Math.floor(value / 100)
    const month = value % 100
    if (month < 1 || month > 12) return ''
    const monthAbbr = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return `${monthAbbr[month]} ${year}`
}

export const formatMonthToSlash = (monthStr: string): string => {
    if (!monthStr) return ''
    const value = parseMonthToValue(monthStr)
    if (!value) return monthStr
    const year = Math.floor(value / 100)
    const month = value % 100
    return `${String(month).padStart(2, '0')}/${year}`
}

export const monthValueToDate = (value: number): Date => {
    if (!value || value < 100) return new Date()
    const year = Math.floor(value / 100)
    const month = value % 100
    return new Date(year, month - 1, 1)
}

export const parseDateString = (dateStr: string): Date => {
    const value = parseMonthToValue(dateStr)
    return monthValueToDate(value)
}

export const formatDateLabel = (label: string): string => {
    if (!label) return ''
    const parts = label.split(' ')
    if (parts.length === 2) {
        const month = parts[0].substring(0, 3)
        const year = parts[1].slice(-2)
        return `${month}-${year}`
    }
    return label
}

export const formatPercentLabel = (v: number | null | undefined): string => {
    if (v === null || v === undefined) return ''
    const n = Number(v)
    if (!Number.isFinite(n)) return ''
    if (n === 0) return '0%'
    if (n > 0 && n < 1) return '<1%'
    return `${Math.round(n)}%`
}

export const COLUMN_CANDIDATES = {
    month: ["survey_month", "survey month", "survey_month_year", "month_year", "Month", "month", "time_period", "time period"],
    questionName: ["question_name", "question name", "questionname", "question", "Question_Name", "Question Name"],
    questionLabel: ["question_label", "question label", "questionlabel", "question_text", "question text", "questiontext", "item_text", "item text", "itemtext", "survey_item_text", "survey item text", "item_label", "item label", "label", "QuestionText", "Question_Text", "Question_Label", "Question Label", "ItemText", "Item"],
    huddle: ["yes_no_huddle", "yes no huddle", "yesnohuddle", "huddle", "huddle_group", "huddlegroup", "Huddle_Group", "Huddle Group"],
    numerator: ["numerator", "Numerator", "num", "count"],
    denominator: ["denominator", "Denominator", "denom", "total"],
    stratification: ["stratification", "Stratification", "strat", "stratum"],
    race: ["race_ethnicity", "race ethnicity", "raceethnicity", "race"],
    education: ["education", "education_level", "educationlevel", "edu"],
    insurance: ["insurance_type", "insurance type", "insurancetype", "insurance"],
    delivery: ["delivery_method", "delivery method", "deliverymethod", "delivery", "delivery_type"],
    complications: ["experienced_complications", "experienced complications", "experiencedcomplications", "complications", "complication"]
}

export const resolveMonthIdx = (columns: string[], rows: any[]): number => {
    const idxByName = findColumnIndex(columns, COLUMN_CANDIDATES.month)
    if (idxByName !== -1) return idxByName

    if (!Array.isArray(rows) || !rows.length) return -1
    const sampleSize = Math.min(rows.length, 200)
    let bestIdx = -1
    let bestScore = -1

    for (let colIdx = 0; colIdx < columns.length; colIdx++) {
        let parsedCount = 0
        const distinct = new Set<number>()

        for (let i = 0; i < sampleSize; i++) {
            const r = rows[i]
            if (!r || colIdx >= r.length) continue
            const mv = parseMonthToValue(r[colIdx])
            if (mv) {
                parsedCount++
                distinct.add(mv)
            }
        }

        const ratio = parsedCount / Math.max(1, sampleSize)
        if (ratio < 0.6 || distinct.size < 2) continue

        const nameBoost = /month|period|time/.test(normalizeKey(columns[colIdx])) ? 10 : 0
        const score = parsedCount + distinct.size * 2 + nameBoost
        if (score > bestScore) {
            bestScore = score
            bestIdx = colIdx
        }
    }

    return bestIdx
}

export const filterRowsByMonthRange = (
    rows: any[],
    monthIdx: number,
    minMonthVal: number,
    maxMonthVal: number
): any[] => {
    if (monthIdx === -1 || (minMonthVal <= 0 && maxMonthVal <= 0)) return rows

    return rows.filter((r: any[]) => {
        const v = parseMonthToValue(r[monthIdx])
        if (v <= 0) return false
        return v >= minMonthVal && v <= maxMonthVal
    })
}
